// ============================================================
// TMR Portal Admin — roles and permissions (10 B7a/B7b)
// Reached only with authorization.admin — see nav.ts/Portal.
// ============================================================
import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Btn } from '../../components/primitives';
import { PageHeader, SearchBox, Table, Td, Drawer, Field } from '../../components/ui';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { EmptyState, ErrorState, Skeleton } from '../../components/states';
import { Icons, StrokeIcon } from '../../lib/icons';
import { usePermissionName } from '../../data/authorization/labels';
import { useAuthorizationRole, useAuthorizationRoles, useDeleteRole } from '../../data/authorization/queries';
import type { AuthorizationRole } from '../../data/authorization/contract';
import { ErrorBanner } from './ErrorBanner';
import { RoleFormModal } from './RoleFormModal';
import { OverlayPortal } from '../../components/OverlayPortal';

/**
 * The roles the platform grants, what each one carries, and where it came from.
 *
 * A role holds permissions directly and inherits more from its parent, and the list keeps the two apart
 * — the drawer shows both sets, because "this role grants X" and "this role's parent grants X" behave
 * differently the day the parent changes.
 *
 * System roles and roles that grant more than the operator holds come back `editable: false`. They stay
 * on screen, read-only, with the reason said: hiding them would misrepresent what exists, and the server
 * is the enforcement either way (Regra 5).
 */
export function RolesPage() {
  const roles = useAuthorizationRoles();
  const deleteRole = useDeleteRole();
  const [search, setSearch] = useState('');
  const [inspecting, setInspecting] = useState<AuthorizationRole | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [toDelete, setToDelete] = useState<AuthorizationRole | null>(null);

  // The endpoint answers with every role at once (it is a short list by nature), so filtering here shows
  // the whole truth rather than a page of it.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = roles.data ?? [];
    return term ? all.filter((role) => role.name.toLowerCase().includes(term)) : all;
  }, [roles.data, search]);

  const nameOf = (roleId: string | null) =>
    roleId ? (roles.data?.find((role) => role.id === roleId)?.name ?? '—') : '—';

  // A failed delete names a role. Once the operator searches, that role may not even be on screen, and
  // the banner becomes a complaint about something they can no longer see.
  useEffect(() => {
    deleteRole.reset();
    // The mutation is stable for the life of the screen; only the search identifies the view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  return (
    <div className="pa-screen" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PageHeader
        eyebrow="Autorização"
        title="Papéis"
        desc="Conjuntos de permissões concedidos às pessoas. Os papéis de sistema são fixos."
        actions={
          <Btn variant="primary" icon={Icons.plus} onClick={() => setCreating(true)}>
            Novo papel
          </Btn>
        }
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SearchBox value={search} onChange={setSearch} placeholder="Buscar papel…" width={320} />
      </div>

      {deleteRole.isError && <ErrorBanner error={deleteRole.error} />}

      {roles.isPending ? (
        <Skeleton rows={6} />
      ) : roles.isError ? (
        <ErrorState error={roles.error} onRetry={() => void roles.refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState
          message={search ? 'Nenhum papel com esse nome.' : 'Nenhum papel cadastrado.'}
          hint={search ? 'Revise o termo buscado.' : 'Crie um papel customizado para começar.'}
        />
      ) : (
        <Table
          columns={[
            { label: 'Papel' },
            { label: 'Herda de' },
            { label: 'Permissões', width: 130 },
            { label: '', align: 'right', width: 170 },
          ]}
        >
          {filtered.map((role) => (
            <tr key={role.id} className="pa-row">
              <Td>
                <button type="button" onClick={() => setInspecting(role)} className="pa-link-btn" style={{ fontSize: 14, fontWeight: 500 }}>
                  {role.name}
                </button>
                {role.isSystem && (
                  <div style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}>papel de sistema</div>
                )}
              </Td>
              <Td>
                <span style={{ fontSize: 13.5, color: 'var(--fg-muted)' }}>{nameOf(role.parentRoleId)}</span>
              </Td>
              <Td>
                <span style={{ fontSize: 13.5 }}>{role.effectivePermissions.length}</span>
                {role.effectivePermissions.length !== role.directPermissions.length && (
                  <span style={{ fontSize: 11.5, color: 'var(--fg-subtle)' }}> ({role.directPermissions.length} diretas)</span>
                )}
              </Td>
              <Td align="right">
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                  {role.editable ? (
                    <>
                      <button type="button" onClick={() => setEditingId(role.id)} className="pa-icon-btn" style={{ width: 30, height: 30 }} title="Editar">
                        <StrokeIcon d={Icons.pen} size={14} />
                      </button>
                      <Btn variant="secondary" size="sm" onClick={() => setToDelete(role)}>
                        Excluir
                      </Btn>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: 'var(--fg-subtle)' }} title={notEditableReason(role)}>
                      somente leitura
                    </span>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Drawer open={Boolean(inspecting)} onClose={() => setInspecting(null)} title={inspecting?.name ?? ''}>
        {inspecting && (
          <>
            <Field label="Tipo">{inspecting.isSystem ? 'Papel de sistema' : 'Papel customizado'}</Field>
            <Field label="Herda de">{nameOf(inspecting.parentRoleId)}</Field>
            <Field label="Edição">{inspecting.editable ? 'Permitida' : notEditableReason(inspecting)}</Field>

            <PermissionList
              title="Permissões diretas"
              empty="Nenhuma permissão concedida diretamente."
              keys={inspecting.directPermissions}
            />
            <PermissionList
              title="Herdadas"
              empty="Nenhuma permissão herdada."
              keys={inspecting.effectivePermissions.filter((key) => !inspecting.directPermissions.includes(key))}
            />
          </>
        )}
      </Drawer>

      {creating && (
        <RoleFormModal role={null} roles={roles.data ?? []} onClose={() => setCreating(false)} />
      )}
      {editingId && (
        <RoleEditor roleId={editingId} roles={roles.data ?? []} onClose={() => setEditingId(null)} />
      )}
      {toDelete && (
        <ConfirmDialog
          title="Excluir papel"
          danger
          message={
            <>
              Excluir <strong>{toDelete.name}</strong>? Só é possível enquanto ninguém tiver este papel
              atribuído e nenhum outro papel herdar dele.
            </>
          }
          confirmLabel="Excluir"
          onConfirm={() => deleteRole.mutate(toDelete.id)}
          onClose={() => setToDelete(null)}
        />
      )}
    </div>
  );
}

/** Why the operator cannot write this role — the two reasons the backend computes `editable` from. */
function notEditableReason(role: AuthorizationRole): string {
  return role.isSystem
    ? 'Papéis de sistema são o vocabulário fixo do produto.'
    : 'Este papel concede permissões que você não possui.';
}

/** Props for {@link RoleEditor}. */
interface RoleEditorProps {
  roleId: string;
  roles: AuthorizationRole[];
  onClose: () => void;
}

/**
 * Opens the editor on a freshly read role.
 *
 * `editable` is computed against the operator's permissions at request time, so a list loaded minutes
 * ago could offer to edit a role they may no longer write. Reading it again costs one request and keeps
 * the form from opening on a promise the server will refuse — which is why the answer is *used* here,
 * and not just fetched: an editor that ignored it would be paying for the round-trip and learning
 * nothing from it.
 *
 * Every state of that request is a state of this modal. Rendering nothing while it is in flight made
 * clicking the pencil look broken — and clicking again is a no-op, since the id has not changed — while
 * a floating banner with no dismissal outlived the click that caused it: `queryClient` disables retry
 * below 500, so a role another admin deleted meanwhile pinned it there for the rest of the session.
 */
function RoleEditor({ roleId, roles, onClose }: RoleEditorProps) {
  const role = useAuthorizationRole(roleId);

  if (role.isPending) {
    return (
      <ModalShell title="Abrindo papel" onClose={onClose}>
        <Skeleton rows={4} />
      </ModalShell>
    );
  }

  if (role.isError) {
    return (
      <ModalShell title="Não foi possível abrir o papel" onClose={onClose}>
        <ErrorBanner error={role.error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
          <Btn variant="primary" onClick={() => void role.refetch()}>Tentar de novo</Btn>
        </div>
      </ModalShell>
    );
  }

  // The list said editable and the server no longer does — the operator's permissions changed under a
  // stale list. Saying which of the two reasons applies beats a form that would 403 on save.
  if (!role.data.editable) {
    return (
      <ModalShell title={role.data.name} onClose={onClose}>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
          {notEditableReason(role.data)}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Btn variant="secondary" onClick={onClose}>Fechar</Btn>
        </div>
      </ModalShell>
    );
  }

  return <RoleFormModal role={role.data} roles={roles} onClose={onClose} />;
}

/** Props for {@link ModalShell}. */
interface ModalShellProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
}

/**
 * The modal frame the editor's own states borrow, so loading and failing land where the form would.
 *
 * Deliberately not the {@link RoleFormModal} chrome: these states have nothing to submit, and giving
 * them a disabled "Salvar" would suggest there is something to save.
 */
function ModalShell({ title, onClose, children }: ModalShellProps) {
  return (
    <OverlayPortal>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(28,30,28,0.32)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div onClick={(e) => e.stopPropagation()} style={{ width: 460, maxWidth: '100%', background: 'var(--cream-50)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', padding: 22 }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 500, fontSize: 16, margin: '0 0 14px', letterSpacing: '-0.01em' }}>{title}</h3>
          {children}
        </div>
      </div>
    </OverlayPortal>
  );
}

/** Props for {@link PermissionList}. */
interface PermissionListProps {
  title: string;
  empty: string;
  keys: string[];
}

/**
 * A titled block of permission keys, in the wording the operator reads.
 *
 * A role carries keys and nothing else — that is the enforcement vocabulary, and the wire shape stays
 * that way — so the names come from the catalogue, which the backend seeds beside each key.
 */
function PermissionList({ title, empty, keys }: PermissionListProps) {
  const permissionName = usePermissionName();

  return (
    <div style={{ marginTop: 18 }}>
      <div className="pa-eyebrow" style={{ marginBottom: 8 }}>{title}</div>
      {keys.length === 0 ? (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-subtle)' }}>{empty}</p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {keys.map((key) => (
            <li key={key} style={{ fontSize: 13.5, display: 'flex', flexDirection: 'column' }}>
              <span>{permissionName(key)}</span>
              <code style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--fg-subtle)' }}>{key}</code>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
